import React, { useState, useEffect, useCallback } from "react";
import buffetVideo from "../assets/buffet-video.mp4";
import { Helmet } from "react-helmet-async";
import { useApi } from "../utils/Api";
import { apiRequest } from "../utils/ApiRequest";
import PricePopup from "../components/PricePopup";

const bookTableImage = "/assets/book-table.webp";

const SLOTS = {
  1: { label: "Slot 1 (2–4pm)", start: "14:00", end: "16:00" },
  2: { label: "Slot 2 (4:30–6:30pm)", start: "16:30", end: "18:30" },
};

const SpinnerOverlay = () => (
  <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-[9999]">
    <div className="border-4 border-white/30 border-t-white rounded-full w-10 h-10 animate-spin" />
  </div>
);

const inputCls = "p-2.5 text-sm border border-[#ccc] rounded-lg outline-none transition-all duration-200 font-[inherit] w-full bg-white text-black placeholder-gray-500 min-w-0 box-border";
const labelCls = "font-medium text-black text-base mb-1 block";

const BuffetBookingPage = () => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [bookingDate, setBookingDate] = useState("");
  const [selectedSlot, setSelectedSlot] = useState("");
  const [numAdults, setNumAdults] = useState(1);
  const [numChildren, setNumChildren] = useState(0);
  const [slots, setSlots] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [dateError, setDateError] = useState("");
  const [showPricePopup, setShowPricePopup] = useState(true);
  const { apiFetch } = useApi();

  const ADULT_PRICE = 24.99;
  const CHILD_PRICE = 12.99;

  const formatDate = useCallback((date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  const isLastSundayOfMonth = useCallback((date) => {
    const test = new Date(date);
    const month = test.getMonth();
    const nextWeek = new Date(test);
    nextWeek.setDate(test.getDate() + 7);
    return nextWeek.getMonth() !== month;
  }, []);

  const isValidBuffetDate = useCallback((date) => {
    const test = new Date(date);
    if (Number.isNaN(test.getTime())) return false;
    return test.getDay() === 0 && isLastSundayOfMonth(test);
  }, [isLastSundayOfMonth]);

  const getNextValidBuffetDate = useCallback(() => {
    const now = new Date();
    const testDate = new Date(now);
    testDate.setHours(0, 0, 0, 0);
    for (let i = 0; i < 400; i++) {
      if (isValidBuffetDate(testDate)) return formatDate(testDate);
      testDate.setDate(testDate.getDate() + 1);
    }
    return "";
  }, [formatDate, isValidBuffetDate]);

  useEffect(() => { setBookingDate(getNextValidBuffetDate()); }, [getNextValidBuffetDate]);

  const handleDateChange = (e) => {
    const value = e.target.value;
    const [year, month, day] = value.split("-").map(Number);
    const chosen = new Date(year, month - 1, day);
    setBookingDate(value);
    if (chosen.getDay() !== 0) setDateError("❌ Please select a Sunday.");
    else if (!isLastSundayOfMonth(chosen)) setDateError("❌ Buffet bookings are only available on the last Sunday of each month.");
    else setDateError("");
  };

  const fetchSlots = useCallback(async (date) => {
    try {
      const data = await apiFetch("get-slot-availability", { booking_date: date });
      if (data.success) setSlots(data.slots);
    } catch (err) { console.error("Error fetching slots:", err); }
  }, [apiFetch]);

  useEffect(() => { if (bookingDate) fetchSlots(bookingDate); }, [bookingDate, fetchSlots]);
  useEffect(() => { if (bookingSuccess) window.scrollTo({ top: 0, behavior: "smooth" }); }, [bookingSuccess]);

  const handleBackToForm = () => {
    setBookingSuccess(false); setName(""); setPhone(""); setEmail("");
    setSelectedSlot(""); setNumAdults(1); setNumChildren(0); setMessage("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setMessage("");
    const selectedDate = new Date(bookingDate);
    if (selectedDate.getDay() !== 0) { setMessage("❌ Please select a Sunday."); return; }
    if (!isLastSundayOfMonth(selectedDate)) { setMessage("❌ Buffet bookings are only available on the last Sunday of each month."); return; }
    const totalPeople = Number(numAdults) + Number(numChildren);
    const selected = slots.find((s) => s.id === parseInt(selectedSlot));
    if (selected && totalPeople > selected.remaining) { setMessage(`❌ Only ${selected.remaining} ${selected.remaining === 1 ? "spot is" : "spots are"} left for this time.`); return; }
    setLoading(true);
    const startTime = Date.now();
    try {
      const result = await apiRequest({ script: "book-buffet", data: { name, phone, email, booking_date: bookingDate, slot_id: parseInt(selectedSlot), num_adults: Number(numAdults) || 1, num_children: Number(numChildren) || 0 } });
      const remaining = Math.max(0, 3000 - (Date.now() - startTime));
      setTimeout(() => {
        setLoading(false);
        if (result.success) {
          const bookingDateFormatted = new Date(bookingDate).toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
          const slotLabel = SLOTS[selectedSlot]?.label || "";
          setMessage(`🎉 Your booking for ${numAdults} adult(s) and ${numChildren} child(ren) on ${bookingDateFormatted} (${slotLabel}) is confirmed. We can't wait to welcome you!`);
          setBookingSuccess(true);
        } else { setMessage(result.message || "Booking failed."); }
      }, remaining);
    } catch (err) {
      const remaining = Math.max(0, 3000 - (Date.now() - startTime));
      setTimeout(() => { setLoading(false); setMessage("An error occurred. Please try again."); }, remaining);
    }
  };

  const isSlotPast = (slotId, date) => {
    if (!date) return false;
    const today = new Date(); const bookingDay = new Date(date);
    if (bookingDay > today) return false;
    if (bookingDay.toDateString() === today.toDateString() && SLOTS[slotId]) {
      const [hours, minutes] = SLOTS[slotId].end.split(":");
      const slotEnd = new Date(bookingDay);
      slotEnd.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      return today > slotEnd;
    }
    return false;
  };

  return (
    <div className="page-container">
      <Helmet>
        <title>Buffet Bookings | LasGidi Restaurant</title>
        <link rel="preload" as="image" href={bookTableImage} fetchpriority="high" />
      </Helmet>

      {showPricePopup && <PricePopup adultPrice={ADULT_PRICE} childPrice={CHILD_PRICE} onClose={() => setShowPricePopup(false)} />}

      {/* Hero banner */}
      <div className="relative w-full h-[200px] mb-10 flex items-center justify-center overflow-hidden">
        <img 
          src={bookTableImage} 
          alt="Book Your Buffet" 
          className="absolute inset-0 w-full h-full object-cover"
          fetchpriority="high"
          loading="eager"
        />
        <div className="absolute inset-0 bg-black/50" />
        <h1 className="relative z-10 text-white text-4xl font-bold">Book Your Buffet</h1>
      </div>

      {bookingSuccess ? (
        <div className="text-center flex flex-col justify-center items-center w-max px-10 py-[30px] mx-auto shadow-[0px_4px_20px_rgba(0,0,0,0.08)] bg-white rounded-[20px]">
          <div className="flex flex-col gap-2.5">
            <h2 className="text-[#28a745] mb-2.5">🎉 Booking Confirmed!</h2>
            <p className="text-base text-black">
              <strong>Name:</strong> {name}<br />
              <strong>Date:</strong> {new Date(bookingDate).toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}<br />
              <strong>Slot:</strong> {SLOTS[selectedSlot]?.label || "N/A"}<br />
              <strong>Adults:</strong> {numAdults}<br />
              <strong>Children:</strong> {numChildren}
            </p>
            <p className="text-base text-black">We can't wait to welcome you!</p>
            <button className="mt-5 px-5 py-2.5 bg-brand-orange text-black font-bold border-none text-base cursor-pointer rounded transition-opacity duration-300 hover:opacity-70 font-[inherit]" onClick={handleBackToForm}>Back</button>
          </div>
        </div>
      ) : (
        <div className="flex bg-black justify-center items-center mx-auto max-w-[1400px] px-[110px] max-lg:px-[50px] max-[420px]:px-[15px] gap-[30px] w-full max-lg:flex-col max-lg:items-center mb-20">
          {/* Video section */}
          <div className="w-full lg:w-[45%] max-w-[450px] relative aspect-[9/16] overflow-hidden flex justify-center items-center rounded-2xl shadow-[0_10px_30px_rgba(0,0,0,0.3)] bg-black">
            <video 
              src={buffetVideo} 
              autoPlay 
              muted 
              loop 
              playsInline 
              controls 
              className="absolute inset-0 w-full h-full object-cover" 
              preload="metadata"
            />
          </div>

          {/* Form section */}
          <div className="max-lg:w-full max-lg:flex max-lg:items-center max-lg:justify-center max-lg:flex-col">
            <div className="w-full max-w-[500px] p-[25px] max-[420px]:p-[15px] bg-white rounded-xl shadow-[0px_4px_20px_rgba(0,0,0,0.08)] box-border">
              {loading && <SpinnerOverlay />}
              <p className="text-center text-black mb-[25px] text-base font-bold">
                Our Buffet is now every last Sunday of the month. Select your preferred slot and join us for a delightful dining experience.
              </p>

              {/* Price summary */}
              <div className="bg-[#fff8e1] border-l-4 border-[#f57c00] px-5 py-[15px] mb-5 rounded-lg shadow-[0_2px_6px_rgba(0,0,0,0.1)]">
                <h3 className="mb-2.5 text-base text-[#f57c00]">Buffet Prices</h3>
                <div className="flex justify-between mb-1 text-base font-medium text-[#333]"><span>Adults:</span><span>£{ADULT_PRICE.toFixed(2)}</span></div>
                <div className="flex justify-between text-base font-medium text-[#333]"><span>Children:</span><span>£{CHILD_PRICE.toFixed(2)}</span></div>
              </div>

              <form className="flex flex-col gap-[18px] mb-5" onSubmit={handleSubmit}>
                <div className="flex flex-col"><label className={labelCls}>Booking Date:</label><input type="date" value={bookingDate} min={getNextValidBuffetDate()} onChange={handleDateChange} disabled={loading} className={inputCls} /></div>
                {dateError && <p className="text-[#e63946] text-lg mt-1 font-bold">{dateError}</p>}
                <div className="flex flex-col"><label className={labelCls}>Name:</label><input type="text" value={name} onChange={(e) => setName(e.target.value)} required disabled={loading} className={inputCls} /></div>

                <div className="flex gap-[15px] flex-col sm:flex-row">
                  <div className="flex-1 flex flex-col"><label className={labelCls}>Phone Number:</label><input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required disabled={loading} className={inputCls} /></div>
                  <div className="flex-1 flex flex-col"><label className={labelCls}>Email Address:</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={loading} className={inputCls} /></div>
                </div>

                <div className="flex flex-col">
                  <label className={labelCls}>Select Slot:</label>
                  <select value={selectedSlot} onChange={(e) => setSelectedSlot(e.target.value)} onClick={() => fetchSlots(bookingDate)} required disabled={loading} className={inputCls}>
                    <option value="">-- Choose a Slot --</option>
                    {slots.filter((s) => s.id !== 3).map((s) => {
                      const slot = SLOTS[s.id]; const full = s.remaining <= 0; const past = isSlotPast(s.id, bookingDate);
                      return <option key={s.id} value={s.id} disabled={full || past}>{slot?.label} {full ? "(Full)" : ""} {past ? "(Over)" : ""}</option>;
                    })}
                  </select>
                </div>

                <div className="flex gap-[15px] flex-col sm:flex-row">
                  <div className="flex-1 flex flex-col"><label className={labelCls}>Number of Adults:</label><input type="number" min="1" value={numAdults} onChange={(e) => setNumAdults(e.target.value === "" ? "" : parseInt(e.target.value, 10))} required disabled={loading} className={inputCls} /></div>
                  <div className="flex-1 flex flex-col"><label className={labelCls}>Number of Children:</label><input type="number" min="0" value={numChildren} onChange={(e) => setNumChildren(e.target.value === "" ? "" : parseInt(e.target.value, 10))} disabled={loading} className={inputCls} /></div>
                </div>

                {message && <p className="text-[#e63946] text-lg font-bold">{message}</p>}
                <button type="submit" disabled={loading || !!dateError} className="mt-5 px-5 py-2.5 bg-brand-orange text-black font-bold border-none text-base cursor-pointer rounded transition-opacity duration-300 hover:opacity-70 disabled:opacity-50 font-[inherit]">
                  {loading ? "Submitting..." : "Submit Booking"}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuffetBookingPage;